import type { CanvasVariant } from "./variant-resolver";
import { cheapestVariant } from "./variant-price";

/**
 * Sizes grouped by aspect ratio.
 *
 * This is how every canvas shop presents the choice, and the reason is the
 * customer's photo: a portrait snap has no business being offered ten
 * landscape sizes. Ratio first, then how big.
 *
 * The ratio is DERIVED from the variant's millimetres — never configured — so
 * an admin cannot get it out of step with the actual print size.
 */

/** Ratios worth naming, longest edge first. */
const KNOWN: Array<{ label: string; value: number }> = [
  { label: "1:1", value: 1 },
  { label: "5:4", value: 5 / 4 },
  { label: "4:3", value: 4 / 3 },
  { label: "3:2", value: 3 / 2 },
  { label: "16:9", value: 16 / 9 },
  { label: "2:1", value: 2 },
];

/** Within this much of a named ratio, call it that. */
const TOLERANCE = 0.03;

function gcd(a: number, b: number): number {
  return b ? gcd(b, a % b) : a;
}

/**
 * Name the ratio of a size, orientation-independent: a 2:3 portrait belongs to
 * the same family as a 3:2 landscape, because the customer rotates the canvas,
 * not the ratio. Splitting them would double every group for no benefit.
 */
export function ratioLabel(widthMm: number, heightMm: number): string {
  const w = Math.max(widthMm, heightMm);
  const h = Math.min(widthMm, heightMm);
  if (!h) return "—";

  const value = w / h;
  const near = KNOWN.find((k) => Math.abs(value - k.value) <= TOLERANCE);
  if (near) return near.label;

  const d = gcd(Math.round(w), Math.round(h)) || 1;
  return `${Math.round(w / d)}:${Math.round(h / d)}`;
}

export interface SizeGroup {
  ratio: string;
  /** Smallest first — the price ladder reads naturally that way. */
  variants: CanvasVariant[];
}

export function groupVariantsByRatio(variants: CanvasVariant[]): SizeGroup[] {
  const groups = new Map<string, CanvasVariant[]>();

  for (const variant of variants ?? []) {
    if (variant.active === false) continue;
    const { widthMm, heightMm } = variant.printSpec ?? {};
    if (!widthMm || !heightMm) continue;
    const key = ratioLabel(widthMm, heightMm);
    groups.set(key, [...(groups.get(key) ?? []), variant]);
  }

  return [...groups.entries()].map(([ratio, list]) => ({
    ratio,
    variants: list
      .slice()
      .sort(
        (a, b) =>
          a.printSpec.widthMm * a.printSpec.heightMm -
          b.printSpec.widthMm * b.printSpec.heightMm,
      ),
  }));
}

/**
 * The group whose shape best matches the uploaded photo, so the editor can
 * preselect it instead of making the customer work out which one fits.
 */
export function bestRatioFor(
  groups: SizeGroup[],
  photoAspect: number,
): string | null {
  if (!groups.length || !Number.isFinite(photoAspect) || photoAspect <= 0) {
    return null;
  }
  const normalised = photoAspect >= 1 ? photoAspect : 1 / photoAspect;

  let best: { ratio: string; distance: number } | null = null;
  for (const group of groups) {
    const first = group.variants[0];
    if (!first) continue;
    const w = Math.max(first.printSpec.widthMm, first.printSpec.heightMm);
    const h = Math.min(first.printSpec.widthMm, first.printSpec.heightMm);
    if (!h) continue;
    const distance = Math.abs(normalised - w / h);
    if (!best || distance < best.distance) best = { ratio: group.ratio, distance };
  }
  return best?.ratio ?? null;
}

/**
 * Groups ordered by how well each shape suits the photo, closest first.
 *
 * This is what lets the editor drop its separate ratio selector. "Razmerje
 * stranic 4:3 / 3:2" asks the customer a question about geometry they did not
 * come here to answer; putting the family that actually fits their photo at the
 * top of one list answers it for them, while still leaving the others reachable
 * for anyone who wants a squarer or wider crop.
 */
export function sortGroupsByFit(
  groups: SizeGroup[],
  photoAspect: number,
): SizeGroup[] {
  if (!Number.isFinite(photoAspect) || photoAspect <= 0) return groups;
  const normalised = photoAspect >= 1 ? photoAspect : 1 / photoAspect;

  const distance = (group: SizeGroup): number => {
    const first = group.variants[0];
    if (!first) return Number.POSITIVE_INFINITY;
    const w = Math.max(first.printSpec.widthMm, first.printSpec.heightMm);
    const h = Math.min(first.printSpec.widthMm, first.printSpec.heightMm);
    if (!h) return Number.POSITIVE_INFINITY;
    return Math.abs(normalised - w / h);
  };

  return groups.slice().sort((a, b) => distance(a) - distance(b));
}

/**
 * A print size written the way the shop talks about it: "40 × 60 cm".
 *
 * Millimetres are the storage unit; nobody shops in them. The rounding matches
 * SizePicker's, so the size quoted in the upload panel's "from" price reads
 * identically to the one the customer later picks from the list.
 */
export function sizeLabel(widthMm: number, heightMm: number): string {
  const cm = (mm: number) => Math.round(mm / 10);
  if (!widthMm || !heightMm) return "";
  return `${cm(widthMm)} × ${cm(heightMm)} cm`;
}

/**
 * Longest edge, in millimetres, a print must reach before the shop will
 * advertise it as an enlargement.
 *
 * 40 cm is where the catalogue's rectangles start. Below it sit the square
 * formats (20 × 20, 30 × 30), which are cheaper per piece and would otherwise
 * win the "from" price — quoting a small square under a headline that promises
 * large photographs undersells what the customer actually came for.
 */
export const HEADLINE_MIN_LONGEST_EDGE_MM = 400;

/** The variants big enough for the headline price to stand behind. */
export function largeFormatVariants(variants: CanvasVariant[]): CanvasVariant[] {
  return (variants ?? []).filter((v) => {
    const { widthMm = 0, heightMm = 0 } = v.printSpec ?? {};
    return Math.max(widthMm, heightMm) >= HEADLINE_MIN_LONGEST_EDGE_MM;
  });
}

/**
 * The size the page opens on and quotes its "from" price for: 40 × 60 cm.
 *
 * Pinned by size rather than derived from price, so repricing a format in the
 * admin cannot quietly move the landing size — 40 × 60 at 19,90 € (85 zł) is
 * the offer the shop advertises.
 */
export const ENTRY_SIZE_MM = { short: 400, long: 600 } as const;

const isEntrySize = (v: CanvasVariant) => {
  const { widthMm = 0, heightMm = 0 } = v.printSpec ?? {};
  return (
    Math.min(widthMm, heightMm) === ENTRY_SIZE_MM.short &&
    Math.max(widthMm, heightMm) === ENTRY_SIZE_MM.long
  );
};

/**
 * The entry enlargement: the active 40 × 60, or — when the catalogue has none —
 * the cheapest large format, then the cheapest of anything.
 */
export function entryVariant(
  variants: CanvasVariant[],
  currency: string,
): CanvasVariant | null {
  const active = (variants ?? []).filter((v) => v.active !== false);
  const pinned = active.find(isEntrySize);
  if (pinned) return pinned;
  const large = largeFormatVariants(active);
  return cheapestVariant(large.length > 0 ? large : active, currency);
}
